import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckoutDeliveryAddressFacade, CheckoutStep, CheckoutStepType } from '@spartacus/checkout/base/root';
import { Address, getLastValueSync, GlobalMessageService, GlobalMessageType, RoutingService, TranslationService, UserAddressService, WindowRef } from '@spartacus/core';
import { Card, LaunchDialogService, getAddressNumbers, ICON_TYPE } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CardWithAddress, CheckoutStepService } from "@spartacus/checkout/base/components";
import { CheckoutPaymentTypeFacade } from "@spartacus/checkout/b2b/root";
import { FormControl, FormGroup, UntypedFormGroup, Validators } from "@angular/forms";
import { formConfig } from '../../../configs/address-details-form.config';
import { User, UserAccountFacade } from "@spartacus/user/account/root";
import { GenFreightForwardFormComponent } from './freight-forward-form/freight-forward-form.component';
import { ViewportScroller } from "@angular/common";
import { GenActiveCartService } from '../../cart/core/facade/active-cart.service';
import { GenAddressFormComponent } from "../../../shared/components/address-form/address-form.component";
import { Cart, OrderEntry } from '@spartacus/cart/base/root';
import { GenLoadingSpinnerService } from 'src/app/generac/services/loading-spinner.service';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { GenShippingGroup, GenSingleConsignmentInfo, GenCartTypes } from 'src/app/generac/models/cart.model';
import { GenCheckPaymentResponse, GenOrderDocument, GenOrderInstructionsNotes, GenSubscriptionInformationItem } from 'src/app/generac/models/checkout.model';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { SubscriptionsService } from 'src/app/generac/services/subscriptions.service';
import { GenCartType } from 'src/app/generac/enums/cart-type.enum';
import { GenAddressFormConfig } from 'src/app/generac/models/address-details.model';
import { GenPaymentTypeEnum } from 'src/app/generac/enums/payment-type.enum';
import { GenAddressValidationService } from 'src/app/generac/services/address-validation.service';
import { GenAddressType } from 'src/app/generac/enums/address-type.enum';
import { GenSubscriptionProduct } from 'src/app/generac/models/subscription.model';
import { GenServiceProductType } from 'src/app/generac/enums/service-product-type';

@Component({
  selector: 'gen-delivery-address',
  templateUrl: './checkout-delivery-address.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenCheckoutDeliveryAddressComponent implements OnInit, OnDestroy {
  @ViewChild('ffAddress') ffAddress: GenFreightForwardFormComponent;
  @ViewChild('dropShipForm') dropShipForm: GenAddressFormComponent;
  GenAddressType = GenAddressType;
  cart: Cart;
  cards$: Observable<CardWithAddress[]>;
  userAccount$: Observable<User> = this.userAccount.get();
  selectedCard: CardWithAddress;
  verifiedAddress: Address;
  isUpdating$: Observable<boolean>;
  formConfig: GenAddressFormConfig[] = formConfig;
  doneAutoSelect = false;
  formGroup: FormGroup;
  isFormInitialized = false;
  fFAddresses: Array<Address> = [];
  fFAddressFormValue: Address;
  selectedFFDropdownValue: any;
  freightForwarderAddress: Address;
  floorPlanErrorMessage: string[];
  isSelling: boolean;
  isSubscription: boolean = false;
  isCartMixed: boolean = false;
  currentStep: CheckoutStep;
  iconTypes = ICON_TYPE;
  internalCalculation: boolean;
  fieldToHighlight: string;
  saveDropShipAddress: boolean;
  limitCountOfAddressesMessage: string;
  subscriptionProducts: GenSubscriptionProduct[];
  checkoutInfo: any;
  subscriptionInfo: GenSubscriptionInformationItem[];
  orderInstructionsNotes: GenOrderInstructionsNotes;
  orderInstructionsControl: FormControl = new FormControl('');
  isServicePartsEnabled: boolean;
  subsShippingGroup: GenShippingGroup;
  checkPaymentResponse: GenCheckPaymentResponse;
  skipPaymentTypeStep: boolean = false;
  builderDocuments: GenOrderDocument[] = [];
  protected busy$ = new BehaviorSubject<boolean>(false);
  protected continueDisabled$ = new BehaviorSubject<boolean>(false);
  private hasToSkipCart = false;
  private onChangeCurrentAddress$: Subscription;
  private subscriptions = new Subscription();
  protected singleConsignmentInfo: GenSingleConsignmentInfo;
  private firstLoad = true;

  get isGuestCheckout(): boolean {
    return !!getLastValueSync(this.activeCartFacade.isGuestCart());
  }

  get selectedAddress$(): Observable<Address | undefined> {
    return this.checkoutDeliveryAddressFacade.getDeliveryAddressState().pipe(
      filter((state) => !state.loading),
      map((state) => {
        if (this.cart?.dropShipAddress) {
          this.verifiedAddress = state.data ? state.data : this.verifiedAddress;
        }
        return state.data;
      }),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
    );
  }

  constructor(
    protected userAddressService: UserAddressService,
    protected checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
    protected activatedRoute: ActivatedRoute,
    protected translationService: TranslationService,
    protected activeCartFacade: GenActiveCartService,
    protected checkoutStepService: CheckoutStepService,
    protected globalMessageService: GlobalMessageService,
    protected checkoutPaymentTypeFacade: CheckoutPaymentTypeFacade,
    protected launchDialogService: LaunchDialogService,
    protected winRef: WindowRef,
    protected genLoadingSpinnerService: GenLoadingSpinnerService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    private validationService: GenAddressValidationService,
    private cdr: ChangeDetectorRef,
    private userAccount: UserAccountFacade,
    private routing: RoutingService,
    private router: Router,
    private checkoutService: GenCheckoutService,
    private subscriptionsService: SubscriptionsService,
    private viewportScroller: ViewportScroller,
  ) {
  }

  ngOnInit(): void {
    this.fieldToHighlight = this.activatedRoute.snapshot.fragment;
    this.currentStep = this.checkoutStepService.allSteps[this.checkoutStepService.getCurrentStepIndex(this.activatedRoute)];
    this.getCards();
    this.initForm();
    this.isUpdating$ = this.createIsUpdating();
    this.resetFFAddress();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getOrderInstructionsNotes(userId: string, cartId: string): Observable<any> {
    return this.checkoutService.getOrderInstructionsNotes(userId, cartId).pipe(
      take(1),
      tap((orderInstructionsNotes: GenOrderInstructionsNotes) => {
        this.orderInstructionsNotes = orderInstructionsNotes;
        this.formGroup.addControl('orderInstructions', this.orderInstructionsControl);
        if (!!orderInstructionsNotes?.required) {
          this.formGroup.get('orderInstructions').addValidators([Validators.required]);
        }
        this.cdr.markForCheck();
      })
    );
  }

  setOrderInstructions(userId: string, cartId: string, instructions: string): Observable<any> {
    return this.checkoutService.setOrderInstructions(userId, cartId, instructions);
  }

  onChangeCurrentAddress(currentAddress: { address: Address }): void {
    this.floorPlanErrorMessage = [];
    this.continueDisabled$.next(true);
    this.checkPaymentResponse = null;

    if (!currentAddress || (currentAddress && currentAddress.address?.addressType?.code === GenAddressType.DROP_SHIP)) {
      return this.onChangeCurrentAddress$?.unsubscribe();
    }
    this.genLoadingSpinnerService.showSpinner();
    this.onChangeCurrentAddress$ = this.checkoutDeliveryAddressFacade.setDeliveryAddress(currentAddress.address)
      .pipe(
        take(1),
        switchMap(() => currentAddress.address?.addressType?.code === GenAddressType.FLOOR_PLAN ?
          this.checkoutService.checkPaymentPossibility(this?.cart?.user?.uid, this?.cart?.code, GenPaymentTypeEnum.WELLS_FARGO).pipe(
            tap((response: GenCheckPaymentResponse) => {
              this.checkPaymentResponse = response;
              if (!response.available && !response.splitPaymentAvailable) {
                this.floorPlanErrorMessage = response.messages;
              }
            })
          ) : of(null)),
        switchMap(() => this.checkoutService
          .getFreightAddress(this?.cart?.user?.uid, this?.cart?.code)
          .pipe(take(1))
        ),
        catchError((err) => {
          this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR);
          this.genLoadingSpinnerService.hideSpinner();
          return EMPTY;
        })
      )
      .subscribe((freight) => {
        this.genLoadingSpinnerService.hideSpinner();
        if (freight.freightForwarderRequirement) {
          if (!freight.addresses) {
            this.globalMessageService.add(
              freight?.message,
              GlobalMessageType.MSG_TYPE_WARNING
            );
          } else {
            this.fFAddresses = freight.addresses;
            this.cdr.detectChanges();
            this.continueDisabled$.next(false);
          }
        } else {
          this.continueDisabled$.next(false);
        }
      });
  }

  resetFFAddress() {
    this.subscriptions.add(
      this.validationService.isAddressChange$
        .pipe(filter(Boolean))
        .subscribe(() => {
          this.fFAddresses = [];
          this.freightForwarderAddress = null;
          this.selectedFFDropdownValue = null;
          if (this.verifiedAddress && this.verifiedAddress.id) {
            try {
              delete this.verifiedAddress['id'];
            } catch (err) {
              console.log(err);
            }
          }
          this.cdr.detectChanges();
        })
    );
  }

  getCardContent(
    address: Address,
    selected: any,
    textDefaultDeliveryAddress: string,
    textShipToThisAddress: string,
    textSelected: string,
    textPhone: string,
    textMobile: string
  ): Card {
    let region = '';
    if (address.region && address.region.isocode) {
      region = address.region.isocode + ', ';
    }

    const numbers = getAddressNumbers(address, textPhone, textMobile);
    return {
      role: 'region',
      title: address.defaultAddress ? textDefaultDeliveryAddress : '',
      textBold: address.firstName + ' ' + address.lastName,
      text: [
        address.line1,
        address.line2,
        address.town + ', ' + region + address.country?.isocode,
        address.postalCode,
        numbers,
      ],
      actions: [{ name: textShipToThisAddress, event: 'send' }],
      header: selected && selected.id === address.id ? textSelected : '',
      label: address.defaultAddress
        ? 'addressBook.defaultDeliveryAddress'
        : 'addressBook.additionalDeliveryAddress',
    } as Card;
  }

  selectAddress(address: Address): void {
    this.globalMessageService.add(
      {
        key: 'checkoutAddress.deliveryAddressSelected',
      },
      GlobalMessageType.MSG_TYPE_INFO
    );

    this.setAddress(address);
  }

  addAddress(address: Address | undefined): void {
    if (!address) {
      return;
    }
    this.busy$.next(true);
    this.doneAutoSelect = true;

    this.subscriptions.add(
      this.checkoutService.setDeliveryAddress(this.selectedCard.address.id, this?.cart?.user?.uid, this.cart.code, address)
        .subscribe(
          () => {
            this.activeCartFacade.reloadActiveCart();
            this.getFreightForwardAddress(address);
          },
          (err) => this.onError())
    );
  }


  getFFCustomeAddress(fFFormValue: any) {
    this.fFAddressFormValue = fFFormValue.ffAddressValue;
    this.selectedFFDropdownValue = fFFormValue.selectedAddress;
  }

  onVerify(address: UntypedFormGroup | null) {
    if (typeof address === 'string') {
      this.verifiedAddress = null;
    } else {
      this.verifiedAddress = address?.getRawValue();
      this.cdr.detectChanges();
      this.showMessage('checkoutAddress.deliveryAddressSelected', GlobalMessageType.MSG_TYPE_INFO);
      this.setCheckoutDeliveryAddress();
    }
  }

  showMessage(key: string, msgType: GlobalMessageType) {
    this.globalMessageService.add({ key: key }, msgType);
  }

  singleConsignmentInfoDataSet(state: boolean) {
    this.singleConsignmentInfo = { state };
  }

  private addYears(date: Date, years: number): Date {
    date.setFullYear(date.getFullYear() + years);
    return date;
  }

  private checkCurrentStepType(type: CheckoutStepType[] | CheckoutStepType): boolean {
    return this.currentStep?.type == type;
  }

  private openSplitPaymentDialog(checkPaymentResponse: GenCheckPaymentResponse) {
    const splitPaymentDialog = this.launchDialogService.openDialog(
      'CHECKOUT_SPLIT_PAYMENT_DIALOG',
      undefined,
      undefined,
      { checkPaymentResponse, cartId: this.cart?.code, userId: this.cart?.user?.uid }
    );

    if (splitPaymentDialog) {
      splitPaymentDialog.pipe(take(1)).subscribe();
    }

    this.subscriptions.add(
      this.launchDialogService.dialogClose
        .pipe(
          tap((reason: string) => {
            if (reason === 'splitPayment') {
              this.activeCartFacade.reloadActiveCart();
              this.continue();
            }
          })
        )
        .subscribe()
    );
  }

  private openFFMixedCartDialog() {
    const ffMixedCartDialog = this.launchDialogService.openDialog(
      'CHECKOUT_FF_MIXED_CART_DIALOG',
      undefined,
      undefined,
    );

    if (ffMixedCartDialog) {
      ffMixedCartDialog.pipe(take(1)).subscribe();
    }

    this.subscriptions.add(
      this.launchDialogService.dialogClose
        .pipe(
          tap((reason: string) => {
            if (reason === 'cancel') {
              this.routing.go('cart');
            } else if (reason === 'ok') {
              if (this.checkPaymentResponse?.splitPaymentAvailable) {
                this.openSplitPaymentDialog(this.checkPaymentResponse);
              } else {
                this.continue();
              }
            }
          })
        )
        .subscribe()
    );
  }

  private continue(): void {
    this.continueDisabled$.next(true);
    this.subscriptions.add(
      this.activeCartFacade
        .takeActive()
        .pipe(
          take(1),
          switchMap((cart: Cart) => {
            return this.userAccount.get().pipe(
              switchMap((user) => {
                if (this.subscriptionProducts?.length && this.formGroup.get(['subscriptions']) && this.checkCurrentStepType(CheckoutStepType.SUBSCRIPTION_INFORMATION)) {
                  let requests: Observable<any>[] = [];
                  let body: GenSubscriptionInformationItem;
                  this.formGroup.getRawValue()?.subscriptions?.forEach((subscription: any) => {
                    body = { ...subscription, subEndDate: this.addYears(subscription?.subEndDate ? new Date(subscription?.subEndDate) : new Date(), 1).toISOString() };
                    const request = this.subscriptionsService.setSubscriptionInformation(
                      user?.uid,
                      cart?.code,
                      body
                    );
                    requests.push(request);
                  });
                  return combineLatest([...requests, this.setCheckoutInfoRequest(user, cart)]);
                } else {
                  return this.setCheckoutInfoRequest(user, cart);
                }
              })
            );
          }),
          switchMap(() => {
            if (this.saveDropShipAddress && this.verifiedAddress && this.cart?.dropShipAddress?.addressType?.code == GenAddressType.DROP_SHIP) {
              if (this.limitCountOfAddressesMessage) {
                this.globalMessageService.add(this.limitCountOfAddressesMessage, GlobalMessageType.MSG_TYPE_WARNING);
                return of({});
              }
              return this.checkoutService.addDropShipAddress(this.cart?.user.uid, this.verifiedAddress).pipe(
                tap(() => {
                  this.showMessage('addressForm.userAddressAddSuccess', GlobalMessageType.MSG_TYPE_CONFIRMATION);
                })
              );
            }
            return of({});
          }),
          switchMap(() => this.setOrderInstructions(this.cart.user.uid, this.cart.code, this.orderInstructionsControl.getRawValue())),
          switchMap(() => {
            this.hasToSkipCart = true;
            const paymentType = this.selectedCard?.address?.addressType?.code === GenAddressType.FLOOR_PLAN ?
              GenPaymentTypeEnum.WELLS_FARGO :
              GenPaymentTypeEnum.CARD;
            return this.checkoutPaymentTypeFacade
              .setPaymentType(paymentType)
              .pipe(
                tap(() => this.setCheckoutDeliveryAddress()),
                catchError((err) => {
                  this.globalMessageService.add(
                    'This material is not currently available to you based on regional restrictions.  Please contact your Sales Representative for more information.',
                    GlobalMessageType.MSG_TYPE_ERROR
                  );
                  this.genLoadingSpinnerService.hideSpinner();
                  return EMPTY;
                }),
              );
          })
        ).subscribe({
          error: () => {
            this.hasToSkipCart = true;
            this.continueDisabled$.next(false);
          },
          complete: () => this.hasToSkipCart = true
        })
    );
  }

  next(): void {
    if (this.formGroup?.getRawValue()) {
      if (this.cart?.cartTypes?.some(ct => ct === GenCartType.FINISHED_GOODS.toString()) && this.cart?.cartTypes?.some(ct => ct === GenCartType.SERVICE_PARTS.toString()) && this.selectedFFDropdownValue) {
        this.openFFMixedCartDialog();
      } else if (this.checkPaymentResponse?.splitPaymentAvailable) {
        this.openSplitPaymentDialog(this.checkPaymentResponse);
      } else {
        this.continue();
      }
    }
  }

  get isBuilderProgram(): boolean {
    return this.cart?.orderDocumentsRequired === true;
  }

  setBuilderDocuments(documents: GenOrderDocument[]): void {
    this.builderDocuments = documents;
    this.cdr.markForCheck();
  }

  setCheckoutInfoRequest(user: User, cart: Cart): Observable<any> {
    return this.checkoutService.setCheckoutInformation(
      {
        purchaseOrderNumber: this.formGroup.get('order')?.get('purchaseOrderNumber')?.value || this.formGroup.get('purchaseOrderNumber').value,
        singleConsignmentInfo: this.singleConsignmentInfo
      },
      user?.uid,
      cart.code,
      cart?.shippingGroups?.[0]?.shippingGroupName,
      cart?.shippingGroups?.[0]?.manufactureGroups?.[0]?.manufactureGroupID
    )
  }

  saveDropShipAddressChanged(value: boolean) {
    this.saveDropShipAddress = value;
  }

  setCheckoutDeliveryAddress() {
    if (!this.selectedCard && ((this.isSubscription || this.isCartMixed) && this.checkCurrentStepType(CheckoutStepType.SUBSCRIPTION_INFORMATION))) {
      this.nextStep();
      return;
    }
    if (this.selectedCard?.address?.addressType?.code !== GenAddressType.DROP_SHIP) {
      const hasFFAddress = this.fFAddresses?.length > 0;
      if (hasFFAddress) {
        this.addFFaddress(this.fFAddressFormValue);
      } else {
        this.setAddress(this.selectedCard.address, !hasFFAddress)
      }
    } else {
      if (this.verifiedAddress?.id) {
        if (this.fFAddressFormValue) {
          this.addFFaddress(this.fFAddressFormValue);
        } else {
          this.nextStep();
        }
      } else {
        this.setAddressedByValue();
      }
    }
  }

  nextStep() {
    this.checkoutStepService.next(this.activatedRoute);

    this.googleAnalyticsService.sendGaEvent('add_shipping_info', {
      currency: this.cart.subTotal.currencyIso,
      value: this.cart.totalPriceWithTax.value,
      orderDiscount: this.cart?.promoDiscounts?.value < 0 ? Math.abs(this.cart?.promoDiscounts?.value) : undefined,
      coupon: this.cart?.appliedVouchers?.length ? this.cart?.appliedVouchers[0].voucherCode : undefined,
      items: this.googleAnalyticsService.buildGaItems(this.cart?.entryGroups, this.cart?.appliedProductPromotions)
    });
  }

  setAddressedByValue() {
    if (this.fFAddresses?.length && this.fFAddressFormValue && this.selectedCard?.address?.addressType?.code !== GenAddressType.DROP_SHIP) {
      this.addFFaddress(this.fFAddressFormValue);
    } else {
      this.addAddress(this.verifiedAddress);
    }
  }

  private addFFaddress(fFAddresses: any): void {
    if (!this.selectedFFDropdownValue) {
      return;
    }
    fFAddresses['originalFreightForwarderAddressId'] = this.selectedFFDropdownValue?.originalFreightForwarderAddressId ?? this.selectedFFDropdownValue.id;
    this.checkoutService.addFFAddress(this.cart?.user?.uid, this.cart.code, fFAddresses)
      .pipe(take(1))
      .subscribe(
        () => this.nextStep(),
        () => this.onError()
      );
  }

  back(): void {
    this.checkoutStepService.back(this.activatedRoute);
  }

  backToSubs(): void {
    this.router.navigate(['/my-account/subscriptions']);
  }

  addressSearchFn(term: string, item: CardWithAddress) {
    const loverTerm = term.toLocaleLowerCase();
    return item?.address?.addressType?.name?.toLocaleLowerCase().includes(loverTerm) ||
      item?.address?.companyName?.toLocaleLowerCase().includes(loverTerm) ||
      item?.address?.formattedAddress?.toLocaleLowerCase().includes(loverTerm);
  }

  private getSubscriptions(cartEntries: OrderEntry[]): void {
    if (this.checkCurrentStepType(CheckoutStepType.SUBSCRIPTION_INFORMATION)) {
      this.subscriptionProducts = this.findSubscriptionsProductInOrderEntries(cartEntries);
    }
  }

  private findSubscriptionsProductInOrderEntries(cartEntries: OrderEntry[]): GenSubscriptionProduct[] {
    return cartEntries?.filter((entry: OrderEntry) =>
      (entry.product as GenSubscriptionProduct)?.serviceProductType &&
      (entry.product as GenSubscriptionProduct)?.sapMaterialNumber &&
      entry.product.serviceProductType !== GenServiceProductType.FLEET &&
      entry.product.serviceProductType !== GenServiceProductType.API_SELLING
    ).map(entry => ({
      ...entry.product as GenSubscriptionProduct,
      quantity: entry.quantity,
      subscriptions: entry.subscriptions,
      entryNumber: entry.entryNumber
    }));
  }

  private getCards(): void {
    this.subscriptions.add(
      this.activeCartFacade
        .getActive()
        .pipe(
          tap((cart: Cart) => {
            this.cart = cart;
            this.orderInstructionsControl.setValue(cart.orderInstructions);
            this.isSubscription = this.hasOnlySubscription(this.cart?.cartTypes);
            this.isCartMixed = this.hasMixedProducts(this.cart?.cartTypes);
            this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
            this.subsShippingGroup = cart?.shippingGroups?.find(sg => sg?.cartTypes[0] == GenCartType.SUBSCRIPTION);
            this.skipPaymentTypeStep = cart?.wellsFargoCart;
            this.checkoutService.disableCheckoutSteps(this.cart?.cartTypes, this.cart?.engineReplacement);
            this.getSubscriptions(this.cart?.entries);
            this.loadAddresses();
            if (this.currentStep.id == 'deliveryAddress' && this.isSubscription) {
              this.routing.go("/checkout/subscription-info");
              this.currentStep = this.checkoutStepService.getCheckoutStep(CheckoutStepType.SUBSCRIPTION_INFORMATION);
            }
            if (this.firstLoad) {
              this.sendBeginCheckoutGaEvent(this.cart);
              this.firstLoad = false;
            }
            this.internalCalculation = this.cart.internalCalculation;
            this.cdr.markForCheck();
            this.cards$ = this.createCards().pipe(
              filter(v => {
                if (this.hasToSkipCart) {
                  this.hasToSkipCart = false;
                  return false;
                }
                return true;
              }),
              map((cards: CardWithAddress[]) => {
                return this.isSubscription
                  ? cards.filter(
                    (card) =>
                      card.address.addressType.code !== GenAddressType.DROP_SHIP
                  )
                  : cards;
              }),
              tap((x) => {
                let dropShipAddress = x.find((y) =>
                  this.cart?.dropShipAddress?.id == y.address.id
                    ? y
                    : !!y.card.header
                );
                let deliveryAddress: Address = cart.deliveryAddress;
                if (dropShipAddress || deliveryAddress?.floorPlanAddress) {
                  if (!this.selectedCard) {
                    this.selectedCard = dropShipAddress || { card: {}, address: deliveryAddress };
                  } else if (
                    this.selectedCard?.address?.addressType?.code ==
                    dropShipAddress?.address?.addressType?.code
                  ) {
                    this.selectedCard = dropShipAddress;
                  }

                  if (this.firstLoad && this.selectedCard.address?.addressType?.code === GenAddressType.FLOOR_PLAN) {
                    this.floorPlanErrorMessage = [];
                    this.checkoutService.checkPaymentPossibility(this?.cart?.user?.uid, this?.cart?.code, GenPaymentTypeEnum.WELLS_FARGO)
                      .subscribe((response: GenCheckPaymentResponse) => {
                        this.checkPaymentResponse = response;
                        if (response.available) {
                          this.checkoutDeliveryAddressFacade.setDeliveryAddress(this.selectedCard.address);
                          return;
                        } else if (!response.available && !response.splitPaymentAvailable) {
                          this.floorPlanErrorMessage = response.messages;
                        }
                        this.genLoadingSpinnerService.hideSpinner();
                        this.continueDisabled$.next(false);
                      })
                  }

                  this.verifiedAddress = deliveryAddress
                    ? deliveryAddress
                    : dropShipAddress.address;

                  if (!this.cart?.freightForwarderAddress) {
                    this.getFreightForwarderAddresses();
                  }
                  if (
                    this.verifiedAddress &&
                    this.cart?.freightForwarderAddress &&
                    !this?.fFAddresses?.length
                  ) {
                    this.freightForwarderAddress = this.cart?.freightForwarderAddress;
                    this.fFAddressFormValue = this.freightForwarderAddress;
                    this.fFAddresses = [this.freightForwarderAddress];
                    this.getFreightForwarderAddresses();
                  }
                } else if (!deliveryAddress) {
                  this.selectedCard = null;
                } else if (!this.selectedCard && deliveryAddress) {
                  this.selectedCard = x.find((y) => deliveryAddress.id == y.address.id);
                  this.getFreightForwarderAddresses();
                }
              })
            );
          }),
          filter(() => !!(this.verifiedAddress || this.cart?.deliveryAddress && this.selectedCard?.address?.addressType?.code !== GenAddressType.DROP_SHIP) && !this.checkCurrentStepType(CheckoutStepType.SUBSCRIPTION_INFORMATION)),
          switchMap(() => this.getOrderInstructionsNotes(this.cart.user?.uid, this.cart?.code))
        )
        .subscribe()
    );
  }

  hasOnlySubscription(cartTypes: GenCartTypes[] | undefined): boolean {
    return cartTypes?.length === 1 && cartTypes?.some(ct => ct === GenCartType.SUBSCRIPTION.toString());
  }

  hasMixedProducts(cartTypes: GenCartTypes[] | undefined): boolean {
    return cartTypes?.length > 1 && cartTypes?.some(ct => ct === GenCartType.SUBSCRIPTION.toString());
  }

  getFreightForwardAddress(address: Address | undefined): void {
    if (!address) {
      return;
    }
    this.subscriptions.add(
      this.checkoutService.getFreightAddress(this?.cart?.user?.uid, this?.cart?.code)
        .pipe(take(1))
        .subscribe((res) => {
          if (res.freightForwarderRequirement) {
            if (!res.addresses) {
              this.globalMessageService.add(res?.message, GlobalMessageType.MSG_TYPE_WARNING);
              this.continueDisabled$.next(true);
            } else {
              this.fFAddresses = res.addresses
              this.cdr.detectChanges();
              this.continueDisabled$.next(false);
            }
          }
        },
          err => this.onError()
        )
    );
  }

  addressLoaded() {
    if (this.fieldToHighlight) {
      this.viewportScroller.scrollToAnchor(this.fieldToHighlight);
      this.fieldToHighlight = null;
    }
  }

  private getFreightForwarderAddresses() {
    if (this.cart?.deliveryAddress || this.cart?.dropShipAddress) {
      this.checkoutService
        .getFreightAddress(this?.cart?.user?.uid, this?.cart?.code)
        .pipe(take(1))
        .subscribe((freight) => {
          if (freight.freightForwarderRequirement) {
            if (!freight.addresses) {
              this.globalMessageService.add(
                freight?.message,
                GlobalMessageType.MSG_TYPE_WARNING
              );
            } else {
              this.fFAddresses = freight.addresses;

              if (!this.compareTwoArrayOfObjects(this.fFAddresses, freight.addresses)) {
                this.fFAddresses = [...this.fFAddresses, ...freight.addresses];
              }
            }
            this.cdr.detectChanges();
          } else {
            this.continueDisabled$.next(false);
          }
        });
    }
  }

  limitCountOfAddressesMessageChange(message: string): void {
    this.limitCountOfAddressesMessage = message;
  }

  compareTwoArrayOfObjects(
    first_array_of_objects: any,
    second_array_of_objects: any
  ) {
    return (
      first_array_of_objects.length === second_array_of_objects.length &&
      first_array_of_objects.every((element_1: any) =>
        second_array_of_objects.some(
          (element_2: any) =>
            element_1.addressType.code === element_2.addressType.code
        )
      )
    );
  };

  protected initForm(): void {
    this.formGroup = new FormGroup({});

    this.subscriptions.add(
      this.activeCartFacade.getActive().pipe(
        take(1)
      ).subscribe(info => {
        this.checkoutInfo = info?.purchaseOrderNumber ? info : { purchaseOrderNumber: this.cart?.contactInformation?.purchaseOrderNumber };
        this.isFormInitialized = true;
        this.cdr.markForCheck();
      })
    );
  }

  protected loadAddresses(): void {
    if (!this.isGuestCheckout && (!this.checkCurrentStepType(CheckoutStepType.SUBSCRIPTION_INFORMATION) ||
      (this.checkCurrentStepType(CheckoutStepType.SUBSCRIPTION_INFORMATION) && this.isSubscription))) {
      this.userAddressService.loadAddresses();
    }
  }

  protected createCards(): Observable<CardWithAddress[]> {
    const translations$ = combineLatest([
      this.translationService.translate('checkoutAddress.defaultDeliveryAddress'),
      this.translationService.translate('checkoutAddress.shipToThisAddress'),
      this.translationService.translate('addressCard.selected'),
      this.translationService.translate('addressCard.phoneNumber'),
      this.translationService.translate('addressCard.mobileNumber'),
    ]);

    return combineLatest([
      this.getSupportedAddresses(),
      translations$])
      .pipe(
        tap(([addresses]) => this.selectDefaultAddress(addresses)),
        map(([addresses, [textDefault, textShipTo, textSelected, textPhone, textMobile]]) =>
          addresses?.map((address) => ({
            address,
            card: this.getCardContent(
              address,
              null,
              textDefault,
              textShipTo,
              textSelected,
              textPhone,
              textMobile
            ),
            disabled: address.erpStatus === 'BLOCK'
          }))
        )
      );
  }

  protected selectDefaultAddress(addresses: Address[], selected?: Address | undefined): void {
    if (
      !this.doneAutoSelect &&
      addresses?.length &&
      (!selected || Object.keys(selected).length === 0)
    ) {
      selected = addresses.find((address) => address.defaultAddress);
      if (selected) {
        this.setAddress(selected);
      }
      this.doneAutoSelect = true;
    }
  }

  protected getSupportedAddresses(): Observable<Address[]> {
    return this.userAddressService.getAddresses() as Observable<Address[]>;
  }

  protected createIsUpdating(): Observable<boolean> {
    return combineLatest([
      this.busy$,
      this.userAddressService.getAddressesLoading(),
      this.getAddressLoading(),
    ]).pipe(
      map(
        ([busy, userAddressLoading, deliveryAddressLoading]) =>
          busy || userAddressLoading || deliveryAddressLoading
      ),
      distinctUntilChanged()
    );
  }

  protected getAddressLoading(): Observable<boolean> {
    return this.checkoutDeliveryAddressFacade.getDeliveryAddressState().pipe(
      map((state) => state.loading),
      distinctUntilChanged()
    );
  }

  protected setAddress(address: Address, shouldRedirect?: boolean): void {
    this.busy$.next(true);
    this.subscriptions.add(this.checkoutDeliveryAddressFacade
      .setDeliveryAddress(address)
      .subscribe({
        complete: () => this.onSuccess(shouldRedirect),
        error: () => this.onError(),
      }));
  }

  protected onSuccess(shouldRedirect?: boolean): void {
    this.busy$.next(false);

    if (shouldRedirect) {
      this.nextStep();
    }
  }

  protected onError(): void {
    this.busy$.next(false);
    this.continueDisabled$.next(false);
  }

  private sendBeginCheckoutGaEvent(cart: Cart): void {
    if (cart) {
      this.googleAnalyticsService.sendGaEvent('begin_checkout', {
        currency: cart.subTotal?.currencyIso,
        value: cart.totalPriceWithTax?.value,
        orderDiscount: this.cart?.promoDiscounts?.value < 0 ? Math.abs(this.cart?.promoDiscounts?.value) : undefined,
        coupon: cart.appliedVouchers?.length ? cart.appliedVouchers[0].voucherCode : undefined,
        items: this.googleAnalyticsService.buildGaItems(cart?.entryGroups, cart?.appliedProductPromotions)
      });
    }
  }
}
